import type { FilterClause } from '@Hooks/useOmniSearch';

/**
 * Reads a (possibly dotted) path from a record. When the path crosses an array, it collects the
 * leaf value from EVERY element (so e.g. `typeMeta.checkItems.name` returns all item names, not
 * just the first).
 */
export function getPath(obj: any, path: string): unknown {
  const segments = path.split('.');
  const walk = (current: any, i: number): any => {
    if (current == null) return undefined;
    if (i === segments.length) return current;
    if (Array.isArray(current)) {
      return current.map((el) => walk(el, i)).filter((v) => v !== undefined);
    }
    return walk(current[segments[i]], i + 1);
  };
  return walk(obj, 0);
}

/** Renders any hit value to a short, human-readable string for table cells / cards. */
export function formatValue(value: unknown): string {
  if (value == null) return '—';
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === 'object' && v ? (v as any).name ?? JSON.stringify(v) : String(v)))
      .join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/** Upsert/remove a single (field, op) clause within a clause list. */
export function setClause(
  clauses: FilterClause[],
  field: string,
  op: FilterClause['op'],
  value: FilterClause['value'] | undefined,
): FilterClause[] {
  const without = clauses.filter((c) => !(c.field === field && c.op === op));
  if (value === undefined || value === '' || value === null) return without;
  return [...without, { field, op, value }];
}

export function getClauseValue(
  clauses: FilterClause[],
  field: string,
  op: FilterClause['op'],
): FilterClause['value'] | undefined {
  return clauses.find((c) => c.field === field && c.op === op)?.value;
}

/** Formats a number as USD without ever rounding (pads to 2 decimals, keeps any extra precision). */
export function formatMoney(value: unknown): string {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return formatValue(value);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 20,
  });
}

/** YYYY-MM-DD → unix seconds (UTC). `endOfDay` pushes to 23:59:59 for upper bounds. */
export function dateToUnix(dateStr: string, endOfDay = false): number {
  const time = endOfDay ? 'T23:59:59Z' : 'T00:00:00Z';
  return Math.floor(new Date(dateStr + time).getTime() / 1000);
}

/** unix seconds → YYYY-MM-DD for prefilling date inputs. */
export function unixToDateInput(value: unknown): string {
  if (typeof value !== 'number') return '';
  const d = new Date(value * 1000);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}
